import { useMemo, useState } from "react";
import { generators, type RequestSpec } from "@rest/codegen";

interface Props {
  spec: RequestSpec;
  initialId?: string;
  onClose: () => void;
}

const STORAGE_KEY = "rest.codegenChoice";

// Per-request "show me this in <language>" picker. Side panel of languages
// on the left, generated code + copy button on the right.
export default function CodeExport({ spec, initialId, onClose }: Props) {
  const stored = (() => { try { return localStorage.getItem(STORAGE_KEY); } catch { return null; } })();
  const [id, setId] = useState<string>(initialId ?? stored ?? "javascript-fetch");
  const [copied, setCopied] = useState(false);

  const active = useMemo(() => generators.find((g) => g.id === id) ?? generators[0], [id]);
  const code = useMemo(() => active.generate(spec), [active, spec]);

  const choose = (newId: string) => {
    setId(newId);
    try { localStorage.setItem(STORAGE_KEY, newId); } catch { /* ignore */ }
  };

  return (
    <div className="fixed inset-0 z-[120] grid place-items-center p-4" style={{ background: "var(--backdrop)" }} onClick={onClose}>
      <div className="card w-full max-w-4xl shadow-[var(--shadow-strong)] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()} style={{ maxHeight: "85vh" }}>
        <div className="flex items-center justify-between border-b border-border px-4 py-3">
          <h2 className="text-lg font-semibold">Copy as code</h2>
          <button className="btn" onClick={onClose}>Close</button>
        </div>

        <div className="flex flex-1 min-h-0">
          <aside className="w-56 shrink-0 border-r border-border bg-surface-2 overflow-y-auto scroll-pane">
            <ul className="p-1 text-sm">
              {generators.map((g) => (
                <li key={g.id}>
                  <button
                    type="button"
                    onClick={() => choose(g.id)}
                    className={
                      "w-full text-left px-3 py-2 rounded-md " +
                      (g.id === id ? "bg-accent text-[var(--ink-on-accent)]" : "text-text-dim hover:bg-surface-3 hover:text-text")
                    }
                  >{g.label}</button>
                </li>
              ))}
            </ul>
          </aside>

          <div className="flex-1 min-w-0 flex flex-col">
            {active.hint && (
              <div className="px-4 py-2 border-b border-border bg-surface-2 text-xs muted-strong">
                <span className="muted">Setup:</span> <code className="mono">{active.hint}</code>
              </div>
            )}
            <pre className="flex-1 overflow-auto scroll-pane p-4 text-[12.5px] leading-[1.55] mono whitespace-pre">{code}</pre>
            <div className="border-t border-border px-3 py-2 flex items-center gap-3">
              {copied && <span className="chip bg-good/15 text-good">Copied!</span>}
              <span className="ml-auto" />
              <button
                type="button"
                className="btn-primary"
                onClick={async () => {
                  try { await navigator.clipboard.writeText(code); setCopied(true); setTimeout(() => setCopied(false), 1500); }
                  catch { /* ignore */ }
                }}
              >Copy to clipboard</button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
